import { useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Send, Sparkles, FileText } from "lucide-react";
import OrderModal from "./OrderModal";

export default function ProductCard({ product, index = 0 }) {
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [orderOpen, setOrderOpen] = useState(false);
    const variant = product.variants[selectedIdx] || { label: "—", price: 0 };

    const tierTone = (() => {
        if (product.tier === "Tier 1") return "bg-highway-red/15 text-highway-red border-highway-red/30";
        if (product.tier === "Tier 2") return "bg-highway-blue/15 text-highway-blue border-highway-blue/30";
        if (product.tier === "Tier 3") return "bg-highway-sunset/15 text-highway-sunset border-highway-sunset/30";
        return "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
    })();

    return (
        <>
            <article
                data-testid={`product-card-${product.id}`}
                className="group fade-up relative bg-card border border-border rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300 hover:-translate-y-1"
                style={{ animationDelay: `${0.05 * (index % 6)}s` }}
            >
                {/* Image */}
                <div className="relative h-56 w-full overflow-hidden">
                    <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card/90 via-card/20 to-transparent" />
                    {product.featured && (
                        <Badge className="absolute top-3 right-3 bg-highway-red text-white border-0 font-mono text-[10px] uppercase tracking-widest">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Featured
                        </Badge>
                    )}
                    {product.tier && (
                        <Badge
                            className={`absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest border ${tierTone}`}
                        >
                            {product.tier}
                        </Badge>
                    )}
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="flex items-start justify-between gap-3">
                        <h3 className="font-display text-xl font-semibold text-white leading-tight">
                            {product.name}
                        </h3>
                        {product.coa_url && (
                            <a
                                data-testid={`coa-${product.id}`}
                                href={product.coa_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="View Certificate of Analysis"
                                className="shrink-0 mt-1 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border border-white/15 text-white/60 hover:text-white hover:border-white/40 transition-colors"
                            >
                                <FileText className="w-3 h-3" />
                                COA
                            </a>
                        )}
                    </div>
                    {product.description && (
                        <p className="mt-2 text-sm text-white/60 leading-relaxed line-clamp-3">
                            {product.description}
                        </p>
                    )}

                    {/* Variant pills */}
                    {product.variants.length > 0 && (
                        <div
                            data-testid={`variants-${product.id}`}
                            className="mt-5 flex flex-wrap gap-2"
                        >
                            {product.variants.map((v, i) => {
                                const active = i === selectedIdx;
                                return (
                                    <button
                                        key={v.label}
                                        data-testid={`variant-${product.id}-${i}`}
                                        onClick={() => setSelectedIdx(i)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider transition-all border ${
                                            active
                                                ? "bg-white text-black border-white"
                                                : "bg-transparent text-white/70 border-white/15 hover:border-white/40 hover:text-white"
                                        }`}
                                    >
                                        {v.label}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Price + CTA */}
                    <div className="mt-6 flex items-end justify-between gap-3">
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                {variant.label}
                            </p>
                            <p
                                data-testid={`price-${product.id}`}
                                className="font-display text-3xl font-bold text-white leading-none"
                            >
                                ${Number(variant.price).toFixed(0)}
                            </p>
                        </div>
                        <Button
                            data-testid={`order-telegram-${product.id}`}
                            size="sm"
                            onClick={() => {
                                setOrderOpen(true);
                            }}
                            className="rounded-full bg-highway-telegram hover:bg-highway-telegram/90 text-white font-semibold px-4 transition-transform hover:-translate-y-0.5"
                        >
                            <Send className="w-3.5 h-3.5 mr-1.5" />
                            Order
                        </Button>
                    </div>
                </div>
            </article>
            <OrderModal
                open={orderOpen}
                onOpenChange={setOrderOpen}
                product={{ ...product, _initialVariantIdx: selectedIdx }}
            />
        </>
    );
}
